import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@tempmee/react';
import { OutlineRefreshIcon } from '@tempmee/react-icons';
import { ListPage } from '../../commonComponents/ListPage';
import { fetchReferrals } from '../../../actions/referralAction';
import { createLoadingSelector } from '../../../apis/selectors';
import { Table } from '../../commonComponents/Table/Table';
import {
  referralColumns,
  referralColumnsMonolith,
  referralRowColorStyles,
  referralRowColorStylesMonolith,
} from '../../commonComponents/jobs-column';
import { Filters, FiltersMonolith } from './Filters';
import { ActionComponent } from './ActionComponent';
import { handleFilterChange } from '../../commonComponents/Table/utils';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FeatureFlagKeys } from '../../../constants/ff.posthog';

const ROWS_PER_PAGE = 15;

const actions = ['GET_REFERRAL', 'CANCEL_REFERRAL'];
const loadingSelector = createLoadingSelector(actions);

export const ReferralList = () => {
  const dispatch = useDispatch();
  // Off is the default, including before PostHog has loaded — this matches
  // fetchReferrals's own default, so the columns/filters shown always match
  // whichever backend the current fetch actually used.
  const useReferralService = useFeatureFlag(FeatureFlagKeys.referralProgramRebuild, false);
  const isLoading = useSelector((state) => loadingSelector(state));
  const referrals = useSelector((state) => state.referral.referrals);
  const searchParams = useSelector((state) => state.referral.searchParams);
  const totalFiltered = useSelector((state) => state.referral.totalReferrals);
  // Superset of both paths' filter fields so a flag flip mid-session doesn't
  // require reshaping this state — fetchReferrals reads only the fields the
  // active path needs and ignores the rest.
  const [filter, setFilter] = useState({
    searchText: searchParams?.searchText || '',
    payFilter: searchParams?.payFilter || 'all',
    status: searchParams?.status || 'all',
    referrerType: searchParams?.referrerType || 'all',
    program: searchParams?.program || 'all',
    isCancelled: searchParams?.isCancelled || 'all',
    page: searchParams?.page || 1,
    sortField: searchParams?.sortField || '',
    order: searchParams?.order || 'desc',
  });
  const totalPages = Math.ceil(totalFiltered / ROWS_PER_PAGE);

  const handleTableChange = (page) => {
    setFilter((prevState) => ({ ...prevState, page }));
  };

  const reloadReferrals = () => {
    dispatch(
      fetchReferrals({
        ...searchParams,
        refresh: 1,
      }),
    );
  };

  useEffect(
    () => () => {
      // cleanup
      dispatch(
        fetchReferrals({
          page: 1,
          limit: 15,
        }),
      );
    },
    [],
  );

  useEffect(() => {
    dispatch(
      fetchReferrals({
        ...filter,
      }),
    );
    // Re-fetch on a flag flip too, so a list already on screen swaps to the
    // other backend's data instead of waiting for the next filter change.
  }, [filter, useReferralService, dispatch]);

  const getUserUrl = (userId, userType) => `/user/${userType.toLowerCase()}/${userId}`;

  const getColumn = () =>
    (useReferralService ? referralColumns : referralColumnsMonolith)({
      getUserUrl,
      ActionComponent: (row) => <ActionComponent row={row} />,
    });

  return (
    <ListPage
      title="Referral"
      isLoading={isLoading}
      total={totalFiltered}
      actions={
        <IconButton
          aria-label="Reload referrals"
          appearance="default"
          Icon={<OutlineRefreshIcon />}
          onClick={reloadReferrals}
        />
      }
      filters={
        useReferralService ? (
          <Filters
            filter={filter}
            handleFilterChange={(type, value) => handleFilterChange(type, value, setFilter)}
          />
        ) : (
          <FiltersMonolith
            filter={filter}
            handleFilterChange={(type, value) => handleFilterChange(type, value, setFilter)}
          />
        )
      }
    >
      <Table
        keyField="referralList.id"
        initialPage={filter.page}
        columns={getColumn()}
        data={referrals}
        noDataComponent="There are no referrals"
        isLoading={isLoading}
        totalPages={totalPages}
        showColumnsToHide={false}
        paginationPerPage={ROWS_PER_PAGE}
        paginationTotalRows={totalFiltered}
        showTotal={false}
        onPageChange={handleTableChange}
        conditionalRowStyles={
          useReferralService ? referralRowColorStyles : referralRowColorStylesMonolith
        }
      />
    </ListPage>
  );
};
